/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {BusinessSettingsDisplayDataModel} from './models/business-settings-display-data.model';
import {EntityTreeBranchTypeEnum} from '../../../../shared/entity-tree/models/entity-tree-branch-type.enum';
import displayDataFunctions from './business-settings-display-data-functions';
import {EntityTreeBranchDisplayDataModel} from './models/entity-tree-branch-display-data.model';
import {EntityTypeEnum} from './entity-type.enum';
import {ContextMenuActionEnum} from '../../../../shared/entity-tree/entity-element/models/context-menu-action.enum';
import {BusinessSettingsBranchDisplayDataModel} from './models/business-settings-branch-display-data.model';

export const BusinessSettingsDisplayData: BusinessSettingsDisplayDataModel = {
    entitiesOrder: {
        commonBranch: new Map<EntityTypeEnum, number>([
            [EntityTypeEnum.COUNTRIES, 1],
            [EntityTypeEnum.PARTNERS, 2],
            [EntityTypeEnum.SHIP_TO_PARTIES, 3],
            [EntityTypeEnum.SUPPLYING_PACKING_PLANTS, 4]
        ]),
        specificBranches: new Map<EntityTreeBranchTypeEnum, Map<EntityTypeEnum, number>>([
            [EntityTreeBranchTypeEnum.SPO, new Map<EntityTypeEnum, number>([
                [EntityTypeEnum.TRANSPORT_MODES, 1],
                [EntityTypeEnum.TRANSPORT_SERVICES, 2],
                [EntityTypeEnum.LEAD_TIMES, 3],
                [EntityTypeEnum.WARNINGS, 4],
                [EntityTypeEnum.REASONS, 5]
            ])],
            [EntityTreeBranchTypeEnum.CLAIM, new Map<EntityTypeEnum, number>([
                [EntityTypeEnum.CLAIM_CODES, 1],
                [EntityTypeEnum.DECISION_CODES, 2],
                [EntityTypeEnum.ERROR_CODES, 3],
                [EntityTypeEnum.BI_CATEGORIES, 4]
            ])],
            [EntityTreeBranchTypeEnum.PNC, new Map<EntityTypeEnum, number>([
                [EntityTypeEnum.TECHNICIANS_GROUPS, 1]
            ])]
        ])
    },
    branchesData: new Map<EntityTreeBranchTypeEnum, BusinessSettingsBranchDisplayDataModel>([
        [EntityTreeBranchTypeEnum.SPO, {
            isActive: false,
            selectTabAction: displayDataFunctions.returnToOriginalOrderBranchSelectAction
        }],
        [EntityTreeBranchTypeEnum.CLAIM, {
            isActive: false,
            selectTabAction: displayDataFunctions.returnToOriginalOrderBranchSelectAction
        }],
        [EntityTreeBranchTypeEnum.PNC, {
            isActive: false,
            selectTabAction: displayDataFunctions.pncBranchSelectAction
        }]
    ]),
    entitiesData: new Map<EntityTypeEnum, EntityTreeBranchDisplayDataModel>([
        [EntityTypeEnum.COUNTRIES, {
            pathTo: [{type: EntityTypeEnum.PARTNERS, active: true}],
            contextMenuActions: [ContextMenuActionEnum.PUT_IN_PATH, ContextMenuActionEnum.EDIT, ContextMenuActionEnum.DELETE],
            hasPath: true,
            hasConnections: false
        }],

        [EntityTypeEnum.PARTNERS, {
            pathTo: [{
                type: EntityTypeEnum.SHIP_TO_PARTIES,
                active: true
            }, {
                type: EntityTypeEnum.SUPPLYING_PACKING_PLANTS,
                active: true
            }],
            pathFrom: [{type: EntityTypeEnum.COUNTRIES, active: true}],
            contextMenuActions: [ContextMenuActionEnum.EDIT, ContextMenuActionEnum.DELETE],
            hasPath: true,
            hasConnections: true
        }],

        [EntityTypeEnum.SHIP_TO_PARTIES, {
            pathFrom: [{type: EntityTypeEnum.PARTNERS, active: true}],
            pathTo: [{type: EntityTypeEnum.TECHNICIANS_GROUPS, active: false}],
            contextMenuActions: [ContextMenuActionEnum.EDIT, ContextMenuActionEnum.DELETE],
            hasPath: false,
            hasConnections: true,
            representParent: true
        }],

        [EntityTypeEnum.SUPPLYING_PACKING_PLANTS, {
            pathFrom: [{type: EntityTypeEnum.PARTNERS, active: true}],
            pathTo: [{
                type: EntityTypeEnum.TRANSPORT_MODES,
                active: false
            }, {
                type: EntityTypeEnum.CLAIM_CODES,
                active: false
            }],
            contextMenuActions: [ContextMenuActionEnum.EDIT, ContextMenuActionEnum.DELETE],
            hasPath: true,
            hasConnections: true,
            representParent: false
        }],

        [EntityTypeEnum.TECHNICIANS_GROUPS, {
            pathFrom: [{type: EntityTypeEnum.SHIP_TO_PARTIES, active: true}],
            contextMenuActions: [ContextMenuActionEnum.EDIT, ContextMenuActionEnum.DELETE],
            hasPath: false,
            hasConnections: true
        }],

        [EntityTypeEnum.TRANSPORT_MODES, {
            pathFrom: [{type: EntityTypeEnum.SUPPLYING_PACKING_PLANTS, active: true}],
            pathTo: [{
                type: EntityTypeEnum.TRANSPORT_SERVICES,
                active: false,
                excludes: EntityTypeEnum.LEAD_TIMES
            }, {
                type: EntityTypeEnum.LEAD_TIMES,
                active: true,
                excludes: EntityTypeEnum.TRANSPORT_SERVICES
            }],
            contextMenuActions: [ContextMenuActionEnum.EDIT, ContextMenuActionEnum.DELETE],
            hasPath: true,
            hasConnections: true
        }],

        [EntityTypeEnum.TRANSPORT_SERVICES, {
            pathFrom: [{type: EntityTypeEnum.TRANSPORT_MODES, active: false}],
            pathTo: [{type: EntityTypeEnum.LEAD_TIMES, active: true}],
            contextMenuActions: [ContextMenuActionEnum.EDIT, ContextMenuActionEnum.DELETE],
            hasPath: true,
            hasConnections: true
        }],

        [EntityTypeEnum.LEAD_TIMES, {
            pathFrom: [{
                type: EntityTypeEnum.TRANSPORT_SERVICES,
                active: false
            }, {
                type: EntityTypeEnum.TRANSPORT_MODES,
                active: true
            }],
            pathTo: [{
                type: EntityTypeEnum.WARNINGS,
                active: false
            }],
            contextMenuActions: [ContextMenuActionEnum.EDIT, ContextMenuActionEnum.DELETE],
            hasPath: true,
            hasConnections: true
        }],

        [EntityTypeEnum.WARNINGS, {
            pathFrom: [{
                type: EntityTypeEnum.LEAD_TIMES,
                active: false
            }],
            contextMenuActions: [ContextMenuActionEnum.EDIT, ContextMenuActionEnum.DELETE],
            hasPath: false,
            hasConnections: true
        }],
        [EntityTypeEnum.REASONS, {
            contextMenuActions: [ContextMenuActionEnum.EDIT, ContextMenuActionEnum.DELETE],
            hasPath: false,
            hasConnections: false
        }],
        [EntityTypeEnum.CLAIM_CODES, {
            pathFrom: [{type: EntityTypeEnum.SUPPLYING_PACKING_PLANTS, active: true}],
            pathTo: [{type: EntityTypeEnum.DECISION_CODES, active: true}],
            contextMenuActions: [ContextMenuActionEnum.EDIT, ContextMenuActionEnum.DELETE],
            hasPath: true,
            hasConnections: true
        }],

        [EntityTypeEnum.DECISION_CODES, {
            pathFrom: [{type: EntityTypeEnum.CLAIM_CODES, active: true}],
            contextMenuActions: [ContextMenuActionEnum.EDIT, ContextMenuActionEnum.DELETE],
            hasPath: false,
            hasConnections: true
        }],

        [EntityTypeEnum.ERROR_CODES, {
            contextMenuActions: [ContextMenuActionEnum.EDIT, ContextMenuActionEnum.DELETE],
            hasPath: false,
            hasConnections: false
        }],

        [EntityTypeEnum.BI_CATEGORIES, {
            contextMenuActions: [ContextMenuActionEnum.EDIT, ContextMenuActionEnum.DELETE],
            hasPath: false,
            hasConnections: false
        }],

        [EntityTypeEnum.TECHNICIANS_GROUPS, {
            pathFrom: [{type: EntityTypeEnum.SHIP_TO_PARTIES, active: true}],
            contextMenuActions: [ContextMenuActionEnum.EDIT, ContextMenuActionEnum.DELETE],
            hasPath: false,
            hasConnections: true
        }]
    ])
};
